/**
 * @fileoverview Client Info Modal Component
 *
 * Modal dialog for viewing client information quickly from anywhere in the portal.
 * Displays all client details including contact info, addresses, project link, and notes.
 *
 * @requires react
 * @requires react-icons/fa
 * @requires react-router-dom
 * @version 1.0.0
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaHome,
  FaStickyNote,
  FaSpinner,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaEdit,
} from "react-icons/fa";
import {
  useAdminClient,
  useCreateClient,
  useUpdateClient,
} from "../../hooks/useAdminClients";
import ClientModal from "./ClientModal";
import { formatPhoneDisplay } from "../../utils/phoneUtils";
import "./ClientInfoModal.css";

/**
 * ClientInfoModal Component
 * @param {Object} props - Component props
 * @param {string} props.clientId - Client record ID
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 */
const PORTAL_ALLOWED_STATUSES = ["Lead", "Active", "In Warranty"];

const ClientInfoModal = ({ clientId, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const {
    data: client,
    isLoading,
    error,
  } = useAdminClient(clientId, {
    enabled: isOpen && !!clientId,
  });
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();

  /**
   * Handle backdrop click - close modal
   */
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("client-modal-overlay")) {
      onClose();
    }
  };

  /**
   * Navigate to project detail page
   */
  const handleViewProject = () => {
    if (client?.projectRecordId) {
      onClose();
      navigate(`/admin/projects/${client.projectRecordId}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="client-modal-overlay" onClick={handleOverlayClick}>
      <div className="client-modal">
        {/* Modal Header */}
        <div className="client-modal-header">
          <div className="client-modal-header-content">
            <div className="client-modal-header-icon">
              <FaUser />
            </div>
            <h2 className="client-modal-title">Client Information</h2>
          </div>
          <div className="client-modal-header-actions">
            {client && !isLoading && (
              <button
                className="client-modal-edit-btn"
                onClick={() => setShowEditModal(true)}
                data-tooltip="Edit Client"
              >
                <FaEdit />
                <span>Edit</span>
              </button>
            )}
            <button
              className="client-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="client-modal-body">
          {isLoading ? (
            <div className="client-modal-loading">
              <FaSpinner className="spinning" />
              <p>Loading client information...</p>
            </div>
          ) : error ? (
            <div className="client-modal-error">
              <FaExclamationTriangle />
              <p>Error loading client: {error.message}</p>
            </div>
          ) : client ? (
            <>
              {/* Client Name */}
              <div className="client-modal-section">
                <h3 className="client-modal-name">{client.clientName}</h3>
                <span
                  className={`client-modal-status ${
                    client.clientStatus === "Active"
                      ? "status-active"
                      : client.clientStatus === "In Warranty"
                        ? "status-warranty"
                        : client.clientStatus === "Lead"
                          ? "status-lead"
                          : client.clientStatus === "Former Client"
                            ? "status-former"
                            : client.clientStatus === "Lead Lost"
                              ? "status-lost"
                              : "status-default"
                  }`}
                >
                  {client.clientStatus}
                </span>
                {(() => {
                  const hasPortalAccess = PORTAL_ALLOWED_STATUSES.includes(
                    client.clientStatus,
                  );
                  return (
                    <span
                      className={`client-modal-status ${
                        hasPortalAccess ? "status-active" : "status-lost"
                      }`}
                      style={{ marginLeft: "8px" }}
                      data-tooltip={
                        hasPortalAccess
                          ? "Client status permits portal sign-in"
                          : "Client status blocks portal sign-in"
                      }
                    >
                      {hasPortalAccess
                        ? " Has Portal Access"
                        : "No Portal Access"}
                    </span>
                  );
                })()}
              </div>

              {/* Primary Contact Information */}
              <div className="client-modal-section">
                <h4 className="client-modal-section-title">
                  Primary Contact Info
                </h4>
                <div className="client-modal-contact-card">
                  <div className="client-modal-info-grid">
                    <div className="client-modal-info-row">
                      <FaEnvelope className="client-modal-icon" />
                      <div>
                        <div className="client-modal-label">Email</div>
                        <div className="client-modal-value">
                          <a
                            href={`mailto:${client.email}`}
                            data-tooltip="Send Email"
                          >
                            {client.email || "—"}
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="client-modal-info-row">
                      <FaPhone className="client-modal-icon" />
                      <div>
                        <div className="client-modal-label">Phone</div>
                        <div className="client-modal-value">
                          <a
                            href={`tel:${client.phone}`}
                            data-tooltip="Call or Text"
                          >
                            {formatPhoneDisplay(client.phone) || "—"}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Secondary Contact Information */}
              {client.secondaryEmail || client.secondaryPhone ? (
                <div className="client-modal-section">
                  <h4 className="client-modal-section-title">
                    Secondary Contact Info
                  </h4>
                  <div className="client-modal-contact-card">
                    <div className="client-modal-info-grid">
                      {client.secondaryEmail && (
                        <div className="client-modal-info-row">
                          <FaEnvelope className="client-modal-icon" />
                          <div>
                            <div className="client-modal-label">
                              Secondary Email
                            </div>
                            <div className="client-modal-value">
                              <a
                                href={`mailto:${client.secondaryEmail}`}
                                data-tooltip="Send Email"
                              >
                                {client.secondaryEmail}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}

                      {client.secondaryPhone && (
                        <div className="client-modal-info-row">
                          <FaPhone className="client-modal-icon" />
                          <div>
                            <div className="client-modal-label">
                              Secondary Phone
                            </div>
                            <div className="client-modal-value">
                              <a
                                href={`tel:${client.secondaryPhone}`}
                                data-tooltip="Call or Text"
                              >
                                {formatPhoneDisplay(client.secondaryPhone)}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Address Information */}
              <div className="client-modal-section">
                <h4 className="client-modal-section-title">Addresses</h4>
                <div className="client-modal-contact-card">
                  <div className="client-modal-info-grid">
                    {client.mailingAddress && (
                      <div className="client-modal-info-row">
                        <FaMapMarkerAlt className="client-modal-icon" />
                        <div>
                          <div className="client-modal-label">
                            Mailing Address
                          </div>
                          <div className="client-modal-value">
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.mailingAddress)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-tooltip="Open in Google Maps"
                            >
                              {client.mailingAddress}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}

                    {client.projectAddress && (
                      <div className="client-modal-info-row">
                        <FaHome className="client-modal-icon" />
                        <div>
                          <div className="client-modal-label">
                            Project Address
                          </div>
                          <div className="client-modal-value">
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.projectAddress)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-tooltip="Open in Google Maps"
                            >
                              {client.projectAddress}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Information */}
              {client.projectName && (
                <div className="client-modal-section">
                  <h4 className="client-modal-section-title">
                    Project Information
                  </h4>
                  <div className="client-modal-project-card">
                    <div>
                      <div className="client-modal-project-name">
                        {client.projectName}
                      </div>
                      {client.projectStatus && (
                        <div className="client-modal-project-status">
                          {client.projectStatus}
                        </div>
                      )}
                    </div>
                    {client.projectRecordId && (
                      <button
                        className="client-modal-project-btn"
                        onClick={handleViewProject}
                      >
                        <span>View Project</span>
                        <FaExternalLinkAlt />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {client.notes && (
                <div className="client-modal-section">
                  <h4 className="client-modal-section-title">
                    <FaStickyNote className="client-modal-icon-inline" />
                    Internal Notes
                  </h4>
                  <div className="client-modal-notes">{client.notes}</div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="client-modal-footer">
          <button
            className="client-modal-btn-secondary"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>
      </div>

      {/* Edit Client Modal */}
      <ClientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        mode="edit"
        recordId={clientId}
        clientData={client}
        mutations={{
          createClient: createClientMutation,
          updateClient: updateClientMutation,
        }}
      />
    </div>
  );
};

export default ClientInfoModal;
